import type { Channel } from '../queries';
import type { QualityCount, QualityReport } from './types';

export type FigurePair = { current: QualityCount; previous: QualityCount };

/** The four unflagged tiles' figures against the period before. */
export type ReportFigures = {
  people: FigurePair;
  visits: FigurePair;
  pageViews: FigurePair;
  averageSeconds: { current: number | null; previous: number | null };
};

export const reportFigures = (report: QualityReport): ReportFigures => {
  const now = report.current.cards.primary;
  const before = report.previous.cards.primary;
  return {
    people: { current: now.people, previous: before.people },
    visits: { current: now.visits, previous: before.visits },
    pageViews: { current: now.pageViews, previous: before.pageViews },
    averageSeconds: { current: now.averageSeconds, previous: before.averageSeconds },
  };
};

export type SourceTile = { channel: Channel; platform: string | null; visitors: number };

/**
 * The sources card: one tile per channel, or with one source picked its top rows and an Other tile for the rest
 * (`cut` when some source names count only in Other). `unavailable` when its credited counts are `null`.
 */
export type SourceCard = { kind: 'unavailable' } | { kind: 'tiles'; tiles: SourceTile[]; other: number; cut: boolean };

const TOP_SOURCES = 5;

const byVisitors = (a: SourceTile, b: SourceTile): number => b.visitors - a.visitors;

export const sourceCard = (report: QualityReport, picked: Channel | null): SourceCard => {
  const { channels, sources, sourcesTruncated } = report.current;
  if (picked === null) {
    const tiles: SourceTile[] = [];
    for (const c of channels) {
      if (c.creditedPeople === null) return { kind: 'unavailable' };
      if (c.creditedPeople > 0) tiles.push({ channel: c.channel, platform: null, visitors: c.creditedPeople });
    }
    return { kind: 'tiles', tiles: tiles.sort(byVisitors), other: 0, cut: false };
  }
  // A channel the answer does not list counts 0; a listed one with a null total is unavailable.
  const channel = channels.find((c) => c.channel === picked);
  const total = channel ? channel.creditedPeople : 0;
  if (total === null) return { kind: 'unavailable' };
  const rows: SourceTile[] = [];
  for (const r of sources) {
    if (r.channel !== picked) continue;
    if (r.creditedPeople === null) return { kind: 'unavailable' };
    rows.push({ channel: r.channel, platform: r.platform, visitors: r.creditedPeople });
  }
  const tiles = rows.sort(byVisitors).slice(0, TOP_SOURCES);
  const named = tiles.reduce((sum, t) => sum + t.visitors, 0);
  return { kind: 'tiles', tiles, other: Math.max(0, total - named), cut: sourcesTruncated };
};
